import { debounce } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Tab from '@mui/material/Tab';
import Card from '@mui/material/Card';
import Tabs from '@mui/material/Tabs';
import Stack from '@mui/material/Stack';
import { LoadingButton } from '@mui/lab';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

import { useBoolean } from 'src/hooks/use-boolean';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { SortOrder } from 'src/services/common.types';
import { SortConfig, PageOptions } from 'src/services/api.types';
import { getRejectApplicationsErrorMessage } from 'src/services/applications/applications.utils';
import { OpenStatus, RejectionReason, ApplicationStage, IApplicationListItem } from 'src/services/applications/applications.types';
import {
  useGetApplicationsLeanQuery,
  useRejectApplicationsMutation,
  useProgressToCandidateMutation,
  useUpdateApplicationRatingMutation,
  useUpdateApplicationOpenStatusMutation,
} from 'src/services/applications/applications.service';

import Iconify from 'src/components/iconify';
import DataList from 'src/components/data-list/data-list';
import RecordPanel from 'src/components/record-panel/record-panel';
import { useRecordPanel } from 'src/components/record-panel/use-record-panel';
import DataListColumnPicker from 'src/components/data-list/data-list-column-picker';
import { useColumnPreferences } from 'src/components/data-list/use-column-preferences';

import ApplicationRejectionConfirm from 'src/sections/jobs/applications/modals/application-reject-confim';

import { buildApplicationColumns } from './application-columns';

const PAGE_SIZE = 50;

const TAB_ALL = 'all';
const TAB_MY_JOBS = 'my_jobs';

/**
 * Org-wide applications list on the shared DataList: every application across the org's live jobs,
 * rather than one job's pipeline at a time.
 *
 * Two record panels are mounted because this surface opens two entity types — the eye opens the
 * APPLICATION (profile/documents/screening), the name opens the CANDIDATE. The candidate panel opts
 * out of the untyped-deep-link fallback so a `?preview` with no `preview_type` opens one, not both.
 */
export default function ApplicationsList() {
  const { t } = useTranslate();
  const { openRecord } = useRecordPanel();
  const authContext = useAuthContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentTab = searchParams.get('tab') === TAB_MY_JOBS ? TAB_MY_JOBS : TAB_ALL;

  // Query state. `loadedPages` sizes the fetch WINDOW rather than selecting one page — see pageOptions.
  const [loadedPages, setLoadedPages] = useState(1);
  const [rows, setRows] = useState<IApplicationListItem[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [filterQuery, setFilterQuery] = useState('');
  const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<SortConfig>({ sort_by: 'last_activity_at', sort_order: SortOrder.DESC });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  /**
   * Infinite scroll grows a window from page 1 instead of requesting page N on its own.
   *
   * The list is a single source of rows for several write surfaces (the table's own star, the
   * record panel's rating, the bulk actions), and each of those invalidates the Applications tag.
   * Requesting one page at a time meant a refetch could only ever refresh THAT page, so a row the
   * user had scrolled past stayed stale until a hard refresh. Fetching the whole loaded window
   * means every refetch returns every visible row.
   */
  const pageOptions: PageOptions = useMemo(
    () => ({ page: 1, per_page: loadedPages * PAGE_SIZE }),
    [loadedPages]
  );

  const { currentData, isFetching } = useGetApplicationsLeanQuery(
    {
      search_query: filterQuery || undefined,
      column_filters: columnSearch,
      my_jobs: currentTab === TAB_MY_JOBS,
      page: pageOptions,
      sortConfig,
    },
    { skip: !authContext.organization?.id, refetchOnMountOrArgChange: true }
  );

  // The response IS the full window, so it replaces the rows outright — no accumulation to merge.
  // Rows stay in local state only so an optimistic write (the star, the New badge) can patch a row
  // without waiting for the refetch that follows it.
  useEffect(() => {
    if (!currentData) return;
    setRows(currentData.results);
  }, [currentData]);

  const count = currentData?.count ?? 0;
  const hasMore = rows.length < count;
  // searchInput !== filterQuery means a keystroke hasn't been sent yet — keep the searching state on.
  const isSearching = isFetching || searchInput !== filterQuery;

  // Collapses the window back to a single page — a new search/sort/tab starts from the top.
  const resetToFirstPage = useCallback(() => setLoadedPages(1), []);

  const applySearch = useMemo(
    () => debounce((value: string) => { setFilterQuery(value); resetToFirstPage(); }, 300),
    [resetToFirstPage]
  );
  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const onSearchChange = useCallback((value: string) => { setSearchInput(value); applySearch(value); }, [applySearch]);

  const onSortChange = useCallback((next: SortConfig) => { setSortConfig(next); resetToFirstPage(); }, [resetToFirstPage]);

  // Per-column search: a blank value drops the key so it's omitted from the request.
  const onColumnSearchChange = useCallback((key: string, value: string) => {
    setColumnSearch((prev) => {
      const next = { ...prev };
      if (value) next[key] = value; else delete next[key];
      return next;
    });
    resetToFirstPage();
  }, [resetToFirstPage]);

  const onLoadMore = useCallback(() => { if (!isFetching && hasMore) setLoadedPages((p) => p + 1); }, [isFetching, hasMore]);

  /**
   * Tab switch merges into the existing params rather than replacing them — this surface also owns
   * ?preview/?preview_type/?preview_mode, and clobbering those would close an open record panel.
   */
  const onTabChange = useCallback((tab: string) => {
    if (searchParams.get('tab') === tab) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
    setSelectedIds([]);
    resetToFirstPage();
  }, [searchParams, setSearchParams, resetToFirstPage]);

  // Selection
  const onSelectRow = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);
  const onSelectAll = useCallback((checked: boolean, ids: string[]) => {
    setSelectedIds(checked ? Array.from(new Set(ids)) : []);
  }, []);

  const [updateOpenStatus] = useUpdateApplicationOpenStatusMutation();
  const [updateRating] = useUpdateApplicationRatingMutation();
  const [progressToCandidate, { isLoading: isProgressing }] = useProgressToCandidateMutation();
  const [rejectApplications, { isLoading: isRejecting }] = useRejectApplicationsMutation();

  const rejectConfirm = useBoolean();

  // The eye opens the application quick view; the name opens the candidate overlay.
  const handleQuickView = useCallback((row: IApplicationListItem) => {
    openRecord('application', row.id, { mode: 'quick' });

    // Viewing the application clears its "New" badge. Updated locally as well as on the server so
    // the badge goes immediately — a refetch would reset the accumulated infinite-scroll pages.
    if (row.open_status === OpenStatus.NEW) {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, open_status: OpenStatus.OPENED } : r)));
      // A failed read-receipt is not worth interrupting the user for.
      updateOpenStatus({ applicationId: row.id, openStatus: OpenStatus.OPENED }).unwrap().catch(() => undefined);
    }
  }, [openRecord, updateOpenStatus]);

  const handleOpenCandidate = useCallback((orgCandidateId: string) => {
    openRecord('candidate', orgCandidateId, { mode: 'overlay' });
  }, [openRecord]);

  /**
   * Writes a star rating from the table.
   *
   * The local row update is not cosmetic: the mutation invalidates the `Applications` tag this list
   * provides, so the star would otherwise not move until the resulting refetch lands. On failure
   * the row is rolled back so the UI never shows a rating the server rejected.
   */
  const handleRatingUpdate = useCallback(async (applicationId: string, rating: number) => {
    const previous = rows.find((r) => r.id === applicationId)?.rating ?? 0;
    setRows((prev) => prev.map((r) => (r.id === applicationId ? { ...r, rating } : r)));

    try {
      await updateRating({ applicationId, rating }).unwrap();
    } catch {
      setRows((prev) => prev.map((r) => (r.id === applicationId ? { ...r, rating: previous } : r)));
      enqueueSnackbar(t('applications.api.update_rating.default_error'), { variant: 'error' });
    }
  }, [rows, updateRating, t]);

  const selectedRows = useMemo(
    () => rows.filter((row) => selectedIds.includes(row.id)),
    [rows, selectedIds]
  );

  /**
   * Groups rows by job, since every bulk endpoint is scoped to a single jobId while a selection here
   * can span jobs. Rows with no resolvable job are dropped — there is nothing to call.
   */
  const groupByJob = useCallback((items: IApplicationListItem[]) => {
    const byJob = new Map<string, string[]>();
    items.forEach((row) => {
      if (!row.job_id) return;
      byJob.set(row.job_id, [...(byJob.get(row.job_id) ?? []), row.id]);
    });
    return byJob;
  }, []);

  /**
   * Fans a bulk action out across jobs. Uses allSettled, not all: one job failing must not discard
   * the jobs that succeeded. Returns the ids that actually went through so the caller can keep the
   * failures selected for a retry.
   */
  const fanOutByJob = useCallback(async (
    byJob: Map<string, string[]>,
    call: (jobId: string, applicationIds: string[]) => Promise<unknown>
  ) => {
    const entries = Array.from(byJob.entries());
    const outcomes = await Promise.allSettled(entries.map(([jobId, ids]) => call(jobId, ids)));

    const succeeded: string[] = [];
    let failedCount = 0;

    outcomes.forEach((outcome, index) => {
      const [, ids] = entries[index];
      if (outcome.status === 'fulfilled') succeeded.push(...ids);
      else failedCount += ids.length;
    });

    return { succeeded, failedCount, jobCount: entries.length };
  }, []);

  const handleProgress = useCallback(async () => {
    // progress-to-candidate means one specific move (Applied -> screening), so anything already
    // past that stage is skipped rather than silently failing server-side.
    const eligible = selectedRows.filter((row) => row.stage === ApplicationStage.APPLIED);
    const skipped = selectedRows.length - eligible.length;

    if (!eligible.length) {
      enqueueSnackbar(
        t('applications.bulk.none_eligible', { defaultValue: 'None of the selected applications are awaiting screening.' }),
        { variant: 'info' }
      );
      return;
    }

    const { succeeded, failedCount, jobCount } = await fanOutByJob(
      groupByJob(eligible),
      (jobId, applicationIds) => progressToCandidate({ jobId, applicationIds }).unwrap()
    );

    if (succeeded.length) {
      // Skipped rows need their own string, not a suffix on the default: `defaultValue` is only
      // consulted when the key is MISSING, so an inline `${skipped} skipped` never reached the user
      // and a skipped row looked identical to a failed one — both stay ticked afterwards.
      enqueueSnackbar(
        skipped
          ? t('applications.bulk.progressed_with_skipped', {
            count: succeeded.length,
            jobs: jobCount,
            skipped,
            defaultValue: `${succeeded.length} progressed across ${jobCount} job(s). ${skipped} skipped — already past screening, and left selected.`,
          })
          : t('applications.bulk.progressed', {
            count: succeeded.length,
            jobs: jobCount,
            defaultValue: `${succeeded.length} progressed across ${jobCount} job(s).`,
          }),
        { variant: 'success' }
      );
    }
    if (failedCount) {
      enqueueSnackbar(
        t('applications.bulk.partial_failure', {
          count: failedCount,
          defaultValue: `${failedCount} could not be progressed and are still selected.`,
        }),
        { variant: 'warning' }
      );
    }

    // Keep the failures selected so a retry re-sends only those.
    setSelectedIds((prev) => prev.filter((id) => !succeeded.includes(id)));
  }, [selectedRows, fanOutByJob, groupByJob, progressToCandidate, t]);

  const handleReject = useCallback(async (reason: RejectionReason, message: string, shouldNotify: boolean) => {
    const { succeeded, failedCount, jobCount } = await fanOutByJob(
      groupByJob(selectedRows),
      (jobId, applicationIds) => rejectApplications({
        jobId,
        applicationIds,
        rejectionFeedback: message,
        rejectionReason: reason,
        shouldNotify,
      }).unwrap()
    );

    if (succeeded.length) {
      enqueueSnackbar(
        t('applications.bulk.rejected', {
          count: succeeded.length,
          jobs: jobCount,
          defaultValue: `${succeeded.length} rejected across ${jobCount} job(s).`,
        }),
        { variant: 'success' }
      );
    }
    if (failedCount) {
      enqueueSnackbar(getRejectApplicationsErrorMessage(new Error('partial')), { variant: 'warning' });
    }

    setSelectedIds((prev) => prev.filter((id) => !succeeded.includes(id)));
    rejectConfirm.onFalse();
  }, [selectedRows, fanOutByJob, groupByJob, rejectApplications, rejectConfirm, t]);

  // Columns
  const registry = useMemo(
    () => buildApplicationColumns({ t, onOpenCandidate: handleOpenCandidate, onRatingUpdate: handleRatingUpdate }),
    [t, handleOpenCandidate, handleRatingUpdate]
  );
  const { columns, pickerModel, save } = useColumnPreferences('applications', registry);

  const renderRowLeading = useCallback((row: IApplicationListItem) => (
    <Tooltip title={t('common.quick_view', { defaultValue: 'Quick view' })}>
      <IconButton size="small" onClick={() => handleQuickView(row)}>
        <Iconify icon="solar:eye-bold" />
      </IconButton>
    </Tooltip>
  ), [handleQuickView, t]);

  const hasSelection = selectedIds.length > 0;

  const toolbar = (
    <Stack sx={{ p: 2, gap: 1 }}>
      <Tabs value={currentTab} onChange={(_e, tab) => onTabChange(tab)}>
        <Tab value={TAB_ALL} label={t('applications.tabs.all', { defaultValue: 'All applications' })} />
        <Tab value={TAB_MY_JOBS} label={t('applications.tabs.my_jobs', { defaultValue: 'My jobs' })} />
      </Tabs>

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        <TextField
          size="small"
          value={searchInput}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('applications.table.search_placeholder', { defaultValue: 'Search applications' })}
          InputProps={{ startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-fill" /></InputAdornment> }}
          sx={{ width: 260 }}
        />

        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <DataListColumnPicker model={pickerModel} onSave={save} />

          <LoadingButton
            variant="contained"
            color="success"
            loading={isProgressing}
            disabled={!hasSelection}
            onClick={handleProgress}
          >
            {t('applications.applicants.progress')}
          </LoadingButton>

          <LoadingButton
            variant="contained"
            color="error"
            loading={isRejecting}
            disabled={!hasSelection}
            onClick={rejectConfirm.onTrue}
          >
            {t('applications.applicants.reject')}
          </LoadingButton>
        </Stack>
      </Stack>
    </Stack>
  );

  return (
    <>
      <Card sx={{ borderRadius: 0 }}>
        <DataList<IApplicationListItem>
          columns={columns}
          rows={rows}
          getRowId={(r) => r.id}
          isSearching={isSearching}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          sort={sortConfig}
          onSortChange={onSortChange}
          columnSearch={columnSearch}
          onColumnSearchChange={onColumnSearchChange}
          toolbar={toolbar}
          selectable
          selectedIds={selectedIds}
          onSelectRow={onSelectRow}
          onSelectAll={onSelectAll}
          renderRowLeading={renderRowLeading}
        />
      </Card>

      <RecordPanel type="application" ids={rows.map((r) => r.id)} total={count} />
      {/* fallback={false}: the application panel owns an untyped ?preview, so both never open at once. */}
      <RecordPanel
        type="candidate"
        ids={rows.map((r) => r.org_candidate_id).filter(Boolean) as string[]}
        fallback={false}
      />

      <ApplicationRejectionConfirm
        open={rejectConfirm.value}
        onClose={rejectConfirm.onFalse}
        onConfirm={handleReject}
        applications={selectedIds}
      />
    </>
  );
}
